import {useState, useEffect} from 'react'
import {CircularProgress, Box, IconButton, Tooltip} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {decode as msgpackDecode} from '@msgpack/msgpack'
import pako from 'pako'

export interface S3Reference {
    bucketName: string
    key: string
    isMsgpack?: boolean,
    isJson?: boolean
}

export interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

export async function fetchS3File(s3Ref: S3Reference, asText = false): Promise<any> {
    const response = await fetch('/api/s3/file', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({...s3Ref, asText})
    })
    if (!response.ok) {
        throw new Error('Failed to fetch S3 file')
    }
    if (s3Ref.isMsgpack) {
        const compressedBuffer = await response.arrayBuffer()
        const decompressed = pako.inflate(new Uint8Array(compressedBuffer))
        return msgpackDecode(decompressed)
    }
    if (asText) {
        return response.text()
    }
    return response.json()
}

export function TabPanel({children, value, index}: TabPanelProps) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box sx={{pt: 2}}>{children}</Box>}
        </div>
    )
}

export function JsonView({data, placeholder}: {data: any, placeholder?: string}) {
    if (!data) {
        return <div style={{color: '#888', fontStyle: 'italic'}}>{placeholder ?? 'No data available'}</div>
    }
    return (
        <pre style={{margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px', background: '#f5f5f5', padding: '12px', borderRadius: '4px'}}>
            {JSON.stringify(data, null, 2)}
        </pre>
    )
}

export function S3DataView({s3Ref, placeholder, asText = false}: {s3Ref?: S3Reference, placeholder?: string, asText?: boolean}) {
    const [data, setData] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        if (!s3Ref?.bucketName || !s3Ref?.key) return

        setLoading(true)
        setError(null)
        fetchS3File(s3Ref, asText)
            .then(result => setData(result))
            .catch((err: any) => setError(err.message))
            .finally(() => setLoading(false))
    }, [s3Ref?.bucketName, s3Ref?.key, asText])

    const handleCopy = () => {
        const textToCopy = asText ? data : JSON.stringify(data, null, 2)
        navigator.clipboard.writeText(textToCopy).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        })
    }

    if (!s3Ref?.bucketName || !s3Ref?.key) {
        return <div style={{color: '#888', fontStyle: 'italic'}}>{placeholder ?? 'No S3 reference available'}</div>
    }

    if (loading) {
        return (
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '12px'}}>
                <CircularProgress size={20} />
                <span>Loading from S3...</span>
            </div>
        )
    }

    if (error) {
        return <div style={{color: 'red'}}>Error: {error}</div>
    }

    return (
        <div style={{position: 'relative'}}>
            {data && (
                <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                    <IconButton
                        size="small"
                        onClick={handleCopy}
                        sx={{position: 'absolute', top: 4, right: 4, zIndex: 1}}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            {asText ? <TextView data={data} placeholder={placeholder} /> : <JsonView data={data} placeholder={placeholder} />}
        </div>
    )
}

export function TextView({data, placeholder}: {data: any, placeholder?: string}) {
    if (!data) {
        return <div style={{color: '#888', fontStyle: 'italic'}}>{placeholder ?? 'No data available'}</div>
    }
    return (
        <pre style={{margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px', background: '#f5f5f5', padding: '12px', borderRadius: '4px', fontFamily: 'monospace'}}>
            {data}
        </pre>
    )
}
